/**
 * Monday adapter for the LEAD push (ARCH-040 §Leads).
 *
 * Writes to the LEADS board (5100796443), where ÖPM and LandVest inbound leads land.
 * Inbound types are separated by the `Lead-Typ` column and by views. Qualification
 * retains the Lead, adds Contact/Company/Deal relations and shares its native
 * activity history with the distinct Deal. The push service resolves an existing
 * item's current board before refreshing Lead columns, including historical moves.
 *
 * Column ids come from `mondayConfigFromEnv` and are pinned in
 * `monday-client.test.ts`.
 *
 * `Leadquelle` IS written, but in its own call — see `pushLead`. Its vocabulary
 * is outbound acquisition channels (E-Mail / LinkedIn / Google Ads / Facebook
 * Ads / Marktrecherche 2024/25 / monday Form, re-read live 2026-08-31), none of
 * which describes a lead the platform produced, so ÖPM adds its own value.
 *
 * Two columns are deliberately NOT written:
 *   - `Status` — the board's own lifecycle column, sales' to own.
 *   - `Erstkontakt am` — the SLA stop clock, set by sales when they call.
 */

import { toCanonicalName } from "@landvest/metadata";
import { MONDAY_HOST, Pointers } from "@oekopunktemarkt/entity-pointers";
import {
  MondayClient,
  axisCells,
  esc,
  mondayConfigFromEnv,
  warnOnce,
  type FetchLike,
  type MondayConfig,
} from "./monday-client";
import {
  NoopCrmLeadAdapter,
  type CrmLead,
  type CrmLeadPort,
  type CrmLeadSyncResult,
} from "./lead-port";
import {
  INITIAL_ADMISSION_MARKER,
  INITIAL_CLARIFICATION_MARKER,
} from "./lead-admission-policy.mjs";

type ColumnValue = string | number | null | { [k: string]: unknown };

/** Application vocabulary and phone validation stay with their existing owners. */
export interface MondayLeadPresentation {
  formatDate(iso: string): string;
  describeLeadDemand(lead: CrmLead): string;
  isHectareProduct(productType: string | null): boolean;
  normalizePhone(raw: string | null | undefined): string | null;
}

/**
 * An ISO timestamp in the shape Monday's `date` column accepts, or null.
 *
 * Monday stores date+time in UTC and renders it in the viewer's timezone, so the
 * UTC parts go across verbatim — converting to Europe/Berlin here would shift
 * every SLA clock by an hour or two. The time is included because it is the
 * point: a Rückruf has a one-hour target, which a date alone cannot express.
 */
export function mondayDateTime(iso: string | null | undefined): ColumnValue | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  const [date, rest] = parsed.toISOString().split("T");
  return { date, time: rest.slice(0, 8) };
}

/**
 * ISO-3166 country for an E.164 number, defaulting to DE.
 *
 * Only the codes this market actually sees; anything else falls back to DE,
 * which is what the column carried unconditionally before. Kept deliberately
 * small — a wrong-but-plausible country is worse than a short list.
 */
const CALLING_CODE_COUNTRY: Array<[string, string]> = [
  ["+49", "DE"],
  ["+43", "AT"],
  ["+41", "CH"],
  ["+31", "NL"],
  ["+32", "BE"],
  ["+33", "FR"],
  ["+39", "IT"],
  ["+45", "DK"],
  ["+48", "PL"],
  ["+420", "CZ"],
  ["+352", "LU"],
];

export function countryOf(e164: string): string {
  const hit = CALLING_CODE_COUNTRY.find(([code]) => e164.startsWith(code));
  return hit?.[1] ?? "DE";
}

function leadColumnValues(
  lead: CrmLead,
  cfg: MondayConfig,
  mode: "create" | "update",
  presentation: MondayLeadPresentation
): Record<string, ColumnValue> {
  const c = cfg.leadColumns;
  const out: Record<string, ColumnValue> = {};
  const refreshAnalysisContact =
    mode === "update" && lead.source.kind === "analysis" && !!lead.sourceKey;

  // `create_item` takes the name as its own argument.
  //
  // On UPDATE the name is only refreshed when the person actually gave us one.
  // A rep who phones „Unbekannter Interessent" (or a card named after a raw
  // phone number), learns the name and renames the card must not have that
  // overwritten by the next republish — the same clobber the deal adapter
  // refuses for `dealStage`. `nameIsPlaceholder` is set by the projection
  // because only it can tell a real answer from a fallback; the adapter used to
  // guess by matching four literals and missed the two commonest cases.
  if (mode === "update" && !lead.nameIsPlaceholder) out.name = lead.name;

  if (c.email && (lead.email || refreshAnalysisContact)) {
    out[c.email] = lead.email ? { email: lead.email, text: lead.email } : null;
  }
  const phone = presentation.normalizePhone(lead.phone);
  if (c.phone && (phone || refreshAnalysisContact)) {
    // Derived, not assumed. `formatPhoneNumber` accepts 80+ calling codes, so a
    // hardcoded "DE" tags an Austrian or Swiss number as German — and a phone
    // value Monday refuses fails the ENTIRE mutation, losing the whole lead.
    out[c.phone] = phone ? { phone, countryShortName: countryOf(phone) } : null;
  }
  if (c.company && (lead.company || refreshAnalysisContact)) out[c.company] = lead.company ?? "";
  // An analysis has its own stable source key. Its corrected contact identity
  // can therefore change without destroying the person-level ÖPM merge index.
  if (refreshAnalysisContact && c.personKey) out[c.personKey] = lead.personKey;

  // Canonicalise before writing a DROPDOWN label: `requests.bundesland` is an
  // unconstrained text column, and Monday rejects the ENTIRE mutation for a
  // label the board does not carry — the lead would silently never appear.
  const canonicalBundesland = lead.bundesland ? toCanonicalName(lead.bundesland) : null;
  if (c.bundesland && canonicalBundesland) {
    out[c.bundesland] = { labels: [canonicalBundesland] };
  }

  if (c.plz && lead.plz) out[c.plz] = lead.plz;
  if (c.ort && lead.ort) out[c.ort] = lead.ort;
  if (c.naturraum && lead.naturraumLabel) out[c.naturraum] = lead.naturraumLabel;
  if (c.deliveryBy && lead.deliveryBy) out[c.deliveryBy] = lead.deliveryBy;

  // Source/type stay CREATE ONLY. ÖPM person keys do too.
  //
  // `ÖPM-Schlüssel` is the key the person-level lookup searches. Rewriting it on
  // an update replaced the form the card was found under, so the same human's
  // next artefact — a contact form, which can only ever produce `mail:` — no
  // longer matched and minted a duplicate. The merge destroyed its own index.
  //
  // `Lead-Typ` is the only thing separating the four inbound streams into
  // views. Re-tagging a card on update dropped an Anfrage out of the Anfragen
  // worklist because the same person later asked for a callback.
  if (mode === "create") {
    // The agent alone links and assigns new Leads. An empty People value avoids
    // inheriting the integration user's ownership from creation defaults.
    if (c.owner) out[c.owner] = { personsAndTeams: [] };
    if (c.sourceKey && lead.sourceKey) out[c.sourceKey] = lead.sourceKey.trim();
    if (c.personKey) out[c.personKey] = lead.personKey;
    if (c.leadType) out[c.leadType] = { label: lead.leadType };
  }

  // `Eingegangen am` starts the SLA clock, and only on CREATE: an update that
  // re-stamped it would hand a three-day-old lead a fresh deadline every time
  // the customer touched their profile.
  if (mode === "create" && c.receivedAt) {
    const received = mondayDateTime(lead.receivedAt);
    if (received) out[c.receivedAt] = received;
  }

  // `!= null`: 0 is a legitimate value, and `undefined` reached Monday as the
  // literal string "undefined" in a numeric column. Stringified, like every
  // other numeric column we write — see `monday-deal-adapter.ts`.
  //
  // The quantity column is titled „Ökopunkteanzahl", so it takes a POINT count
  // and nothing else. Writing it for every product put a hectare figure under a
  // points heading on all 38 Ausgleichsflächen-Anfragen — a 3,7-hectare request
  // read as 3,7 Ökopunkte.
  //
  // The complement matters as much: the board has two numeric slots, points and
  // area, and between them they should carry the volume of every product.
  // `isHectareProduct` routes the three Hektar products to the area column.
  //
  // One case still reaches the board with neither, and deliberately: an Angebot
  // for a hectare product whose `area_hectares` is null AND whose `price_unit`
  // does not say per-hectare. There the projection holds a number it cannot name
  // — live chatbot rows put POINT counts in that field — so it declines to guess
  // rather than write 260 km² into an area column.
  if (c.quantity && lead.quantity != null && !presentation.isHectareProduct(lead.productType)) {
    out[c.quantity] = String(lead.quantity);
  }
  if (c.areaSqm && lead.areaSqm != null) out[c.areaSqm] = String(lead.areaSqm);
  if (c.buyerPrice && lead.maxPrice != null) out[c.buyerPrice] = String(lead.maxPrice);

  // Achse / Subjekt / Einheit, as on the Deals board: only labels the board
  // carries (an unknown one would reject the whole mutation), and only when
  // the source knows its axes — an ÖPM lead without them leaves the cells alone.
  if (lead.axes?.length) {
    const cells = axisCells(lead.axes);
    if (c.axis && cells.axis) out[c.axis] = cells.axis;
    if (c.unit && cells.unit) out[c.unit] = cells.unit;
    if (c.subject && cells.subject) out[c.subject] = cells.subject;
  }

  return out;
}

/** The German headline for each inbound type. */
const LEAD_HEADLINE: Record<CrmLead["leadType"], string> = {
  "LandVest-Analyse": "Neue Flächenanalyse (LandVest)",
  Anfrage: "Neue Anfrage über den Marktplatz (ÖPM)",
  Angebot: "Neues Angebot über den Marktplatz (ÖPM)",
  Rückruf: "Rückruf angefordert (Klara-Chat)",
  Kontaktformular: "Neue Nachricht über das Kontaktformular",
};

/** An e-mail carries the same Lead-Typ as a marketplace artefact but arrived another way. */
const EMAIL_HEADLINE: Partial<Record<CrmLead["leadType"], string>> = {
  Anfrage: "Neue Anfrage per E-Mail",
  Angebot: "Neues Angebot per E-Mail",
};

/**
 * Total by construction: `LeadType` is exhaustive today, but the board carries
 * a fifth label and widening the union is one edit away — an unmapped type
 * would otherwise throw inside `esc` and ship a lead card with no body at all.
 */
function headlineOf(lead: CrmLead): string {
  const byType = lead.source.kind === "email" ? EMAIL_HEADLINE : LEAD_HEADLINE;
  return byType[lead.leadType] ?? LEAD_HEADLINE[lead.leadType] ?? "Neuer Lead (ÖPM)";
}

/** Where `adminUrl` leads, named for the person reading the card. */
const ADMIN_LINK_LABEL: Partial<Record<CrmLead["source"]["kind"], string>> = {
  analysis: "In LandVest öffnen",
  email: "Im E-Mail-Intake öffnen",
};

/** Callbacks and contact submissions have no admin page: no link rather than one that 404s. */
function renderAdminLink(lead: CrmLead): string {
  if (!lead.adminUrl) return "";
  const label = ADMIN_LINK_LABEL[lead.source.kind] ?? "In ÖPM öffnen";
  return `<p><a href="${esc(lead.adminUrl)}">${esc(label)}</a></p>`;
}

/**
 * The item body — what the person wants, in their words, plus the way back into
 * ÖPM and the known-contact flag.
 *
 * The flag lives here rather than in a column because the Leads board still has
 * no free-text column for it. That makes it visible but NOT filterable.
 */
export function renderLeadSummary(
  lead: CrmLead,
  contactUrl: string | null,
  presentation: MondayLeadPresentation
): string {
  const known = contactUrl
    ? `<p><b>⚠️ Bestandskontakt</b> — diese Person ist bereits in Kontakte angelegt. ` +
      `<a href="${esc(contactUrl)}">Kontakt öffnen</a><br/>` +
      `Hinweis aus dem Quellsystem; die Zuordnung prüft der Lead Agent.</p>`
    : "";

  // What the person actually wrote — the chat intent, the form message, the
  // seller's ask. For a Rückruf it is the only substance there is, so it comes
  // before everything except the headline.
  const note = lead.note ? `<p>${esc(lead.note)}</p>` : "";

  const link = renderAdminLink(lead);

  // A Rückruf has no product, volume or price, so the demand line would be
  // empty — and a headline followed by a blank line reads as a rendering bug.
  const title = headlineOf(lead);
  const demand = presentation.describeLeadDemand(lead);
  const headline = demand
    ? `<p><b>${esc(title)}</b><br/>${esc(demand)}</p>`
    : `<p><b>${esc(title)}</b></p>`;

  return headline + note + known + link;
}

/** Canonical pointer, or undefined when either id is not numeric. */
function mondayPointer(boardId: string, itemId: string): string | undefined {
  try {
    return Pointers.monday.item(boardId, itemId);
  } catch {
    // Per ITEM — `warnOnce` would never dedupe it, only leak a key per lead.
    console.warn(
      `[Monday] could not build a pointer for lead board ${boardId} item ${itemId} — the lead will not be linked`
    );
    return undefined;
  }
}

/**
 * Compare rendered content: Monday normalises its rich-text HTML, so raw body
 * equality is not stable across a write/read round-trip.
 */
function summaryText(body: string): string {
  return body
    .split(INITIAL_ADMISSION_MARKER)[0]
    .split(INITIAL_CLARIFICATION_MARKER)[0]
    .replace(/<a\b[^>]*href=["']([^"']+)["'][^>]*>/gi, "$1 ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

/** Check every update page: a sales reply must not hide an earlier delivery. */
async function hasSummary(client: MondayClient, itemId: string, body: string): Promise<boolean> {
  for (let page = 1; ; page += 1) {
    const data = (await client.gql(
      `query($i:[ID!]!,$p:Int!){ items(ids:$i){ updates(limit:100,page:$p){ body } } }`,
      { i: [itemId], p: page }
    )) as { items?: Array<{ updates?: Array<{ body?: string }> }> } | null;
    const updates = data?.items?.[0]?.updates;
    if (!Array.isArray(updates))
      throw new Error(`Monday did not return updates for lead ${itemId}`);
    if (updates.some((update) => summaryText(update.body ?? "") === summaryText(body))) return true;
    if (updates.length < 100) return false;
  }
}

async function postSummary(
  client: MondayClient,
  itemId: string,
  lead: CrmLead,
  contactUrlFor: (contactId: string) => string,
  presentation: MondayLeadPresentation,
  recoverable: boolean
): Promise<void> {
  try {
    const body = renderLeadSummary(
      lead,
      lead.knownContactId ? contactUrlFor(lead.knownContactId) : null,
      presentation
    );
    if (recoverable && (await hasSummary(client, itemId, body))) return;
    const posted = (await client.gql(
      `mutation($i:ID!,$b:String!){ create_update(item_id:$i,body:$b){ id } }`,
      {
        i: itemId,
        b: body,
      }
    )) as { create_update?: { id?: string } } | null;
    if (recoverable && !posted?.create_update?.id)
      throw new Error(`Monday did not record the summary on item ${itemId}`);
  } catch (e) {
    // A keyed lead can safely retry; a legacy unkeyed create cannot.
    if (recoverable) throw e;
    console.warn(
      `[Monday] lead ${itemId} saved but its summary failed: ${e instanceof Error ? e.message : String(e)}`
    );
  }
}

/** Fail closed: a malformed lookup must never become permission to create. */
export async function findLeadBySourceKey(
  client: MondayClient,
  boardId: string,
  columnId: string,
  key: string
): Promise<string | null> {
  const data = (await client.gql(
    `query($b:ID!,$c:String!,$v:[String!]!){
      items_page_by_column_values(board_id:$b,limit:2,columns:[{column_id:$c,column_values:$v}]){
        items { id board { id } }
      }
    }`,
    { b: boardId, c: columnId, v: [key] }
  )) as {
    items_page_by_column_values?: { items?: Array<{ id: string; board?: { id: string } }> };
  } | null;
  const items = data?.items_page_by_column_values?.items;
  if (!Array.isArray(items)) throw new Error("Monday did not return the source-key lookup");
  if (items.length > 1) throw new Error(`Multiple Monday leads carry source key ${key}`);
  if (!items.length) return null;
  if (String(items[0].board?.id) !== boardId)
    throw new Error("Monday source-key match belongs to another board");
  if (!items[0].id) throw new Error("Monday source-key match has no item id");
  return String(items[0].id);
}

/** A placeholder may replace a stale name only after comparing contact identity. */
async function readLeadPersonKey(
  client: MondayClient,
  boardId: string,
  itemId: string,
  columnId: string | undefined
): Promise<string | null> {
  if (!columnId) throw new Error("Monday analysis contact identity column is not configured");
  const data = (await client.gql(
    `query($i:[ID!]!,$c:[String!]!){ items(ids:$i){ id board { id } column_values(ids:$c){ id text } } }`,
    { i: [itemId], c: [columnId] }
  )) as {
    items?: Array<{
      id: string;
      board?: { id: string };
      column_values?: Array<{ id: string; text?: string | null }>;
    }>;
  } | null;
  const item = data?.items?.[0];
  const column = item?.column_values?.find((value) => value.id === columnId);
  if (
    data?.items?.length !== 1 ||
    String(item?.id) !== itemId ||
    String(item?.board?.id) !== boardId ||
    !column
  ) {
    throw new Error("Monday did not return the existing analysis contact identity");
  }
  return column.text?.trim() || null;
}

/**
 * The body for a DIFFERENT artefact landing on an existing person's card.
 *
 * Deliberately says which artefact and when, because the card's own columns
 * still describe the earlier one — this is the only record that the second
 * thing happened at all.
 */
export function renderLeadAnnotation(lead: CrmLead, presentation: MondayLeadPresentation): string {
  const when = lead.receivedAt ? presentation.formatDate(lead.receivedAt) : null;
  const demand = presentation.describeLeadDemand(lead);

  const link = renderAdminLink(lead);

  return (
    `<p><b>${esc(headlineOf(lead))}</b>` +
    `${when ? ` — ${esc(when)}` : ""}<br/>` +
    `<i>Dieselbe Person, neuer Vorgang. Die Spalten dieser Karte gehören zum ` +
    `vorherigen Vorgang und bleiben unverändert.</i></p>` +
    (demand ? `<p>${esc(demand)}</p>` : "") +
    (lead.note ? `<p>${esc(lead.note)}</p>` : "") +
    link
  );
}

export function createMondayLeadAdapter(
  cfg: MondayConfig,
  fetchFn: FetchLike,
  /** Base URL for a Kontakte item, so the body can link the known contact. */
  contactUrlFor: (contactId: string) => string,
  presentation: MondayLeadPresentation
): CrmLeadPort {
  const client = new MondayClient(cfg, fetchFn);
  const boardId = cfg.leadsBoardId;

  return {
    async pushLead(lead: CrmLead, existingItemId: string | null): Promise<CrmLeadSyncResult> {
      if (!boardId) {
        // Deals can be configured without leads. Say so once rather than failing
        // every inbound lead.
        warnOnce(
          "leads-board-unset",
          "[Monday] MONDAY_LEADS_BOARD_ID is not set — inbound leads are not pushed"
        );
        return { ok: true };
      }

      const sourceKey = lead.sourceKey?.trim();
      const sourceKeyColumn = cfg.leadColumns.sourceKey;
      if (lead.sourceKey !== undefined && (!sourceKey || !sourceKeyColumn)) {
        return {
          ok: false,
          error:
            "A source key and MONDAY_LEAD_COL_SOURCE_KEY are required for recoverable lead delivery",
        };
      }
      const recoverable = Boolean(sourceKey);
      let itemId = existingItemId;
      try {
        if (!itemId && sourceKey && sourceKeyColumn) {
          itemId = await findLeadBySourceKey(client, boardId, sourceKeyColumn, sourceKey);
        }
        const alreadyExisted = Boolean(itemId);
        if (itemId) {
          const columns = leadColumnValues(lead, cfg, "update", presentation);
          if (sourceKey && lead.source.kind === "analysis" && lead.nameIsPlaceholder) {
            const previousIdentity = await readLeadPersonKey(
              client,
              boardId,
              itemId,
              cfg.leadColumns.personKey
            );
            if (previousIdentity !== lead.personKey) columns.name = lead.name;
          }
          const updated = (await client.gql(
            `mutation($b:ID!,$i:ID!,$c:JSON!){
              change_multiple_column_values(board_id:$b,item_id:$i,column_values:$c){ id }
            }`,
            {
              b: boardId,
              i: itemId,
              c: JSON.stringify(columns),
            }
          )) as { change_multiple_column_values?: { id?: string } } | null;
          if (!updated?.change_multiple_column_values?.id) {
            throw new Error(`Monday did not apply the update to lead item ${itemId}`);
          }
        } else {
          const columns = leadColumnValues(lead, cfg, "create", presentation);
          try {
            const data = (await client.gql(
              `mutation($b:ID!,$n:String!,$c:JSON!){ create_item(board_id:$b,item_name:$n,column_values:$c){ id } }`,
              {
                b: boardId,
                n: lead.name,
                c: JSON.stringify(columns),
              }
            )) as { create_item?: { id?: string } } | null;
            itemId = data?.create_item?.id ?? null;
            if (!itemId) throw new Error("Monday create_item returned no id");
          } catch (error) {
            // A lost response does not mean the write failed. Re-query instead
            // of repeating a create; the outbox will retry if still uncertain.
            if (sourceKey && sourceKeyColumn) {
              itemId = await findLeadBySourceKey(client, boardId, sourceKeyColumn, sourceKey);
            }
            if (!itemId) throw error;
          }
        }

        const origin = lead.sourceSystem ?? cfg.leadSourceLabel;
        // Scoped to Leadquelle so only its controlled vocabulary may grow.
        // Recoverable deliveries also retry origin on existing items.
        if ((!alreadyExisted || recoverable) && cfg.leadColumns.leadSource && origin) {
          try {
            const result = (await client.gql(
              `mutation($b:ID!,$i:ID!,$c:String!,$v:JSON!){
                change_column_value(board_id:$b,item_id:$i,column_id:$c,value:$v,create_labels_if_missing:true){ id }
              }`,
              {
                b: boardId,
                i: itemId,
                c: cfg.leadColumns.leadSource,
                v: JSON.stringify({ label: origin }),
              }
            )) as { change_column_value?: { id?: string } } | null;
            if (recoverable && !result?.change_column_value?.id)
              throw new Error(`Monday did not apply Leadquelle to lead ${itemId}`);
          } catch (error) {
            if (recoverable) throw error;
            console.warn(
              `[Monday] lead ${itemId} created but Leadquelle was not set: ${error instanceof Error ? error.message : String(error)}`
            );
          }
        }
        await postSummary(client, itemId, lead, contactUrlFor, presentation, recoverable);
        return {
          ok: true,
          externalItemId: itemId,
          externalItemUrl: mondayPointer(boardId, itemId),
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error(`[Monday] pushLead ${lead.source.kind}/${lead.source.id} failed:`, message);
        return {
          ok: false,
          error: message,
          ...(recoverable && itemId
            ? { externalItemId: itemId, externalItemUrl: mondayPointer(boardId, itemId) }
            : {}),
        };
      }
    },

    async annotateLead(lead: CrmLead, itemId: string): Promise<CrmLeadSyncResult> {
      if (!boardId) return { ok: true };

      try {
        // NO `change_multiple_column_values`. The card's columns belong to a
        // different artefact; writing this one's over them produced cards like
        // „Bayern / 14467 / 200 Punkte" — a request nobody filed. An update is
        // additive, so there is nothing to corrupt.
        const posted = (await client.gql(
          `mutation($i:ID!,$b:String!){ create_update(item_id:$i,body:$b){ id } }`,
          { i: itemId, b: renderLeadAnnotation(lead, presentation) }
        )) as { create_update?: { id?: string } | null };

        // Unlike the summary on create, this is the ONLY record that the second
        // artefact exists — no column carries it — so a failure here is a real
        // failure and must be retried, not warned about.
        if (!posted?.create_update?.id) {
          return { ok: false, error: `Monday did not record the update on item ${itemId}` };
        }

        return {
          ok: true,
          externalItemId: itemId,
          externalItemUrl: mondayPointer(boardId, itemId),
        };
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        console.error(
          `[Monday] annotateLead ${lead.source.kind}/${lead.source.id} failed:`,
          message
        );
        return { ok: false, error: message };
      }
    },
  };
}

export function getMondayLeadAdapter(
  presentation: MondayLeadPresentation,
  env: Record<string, string | undefined> = process.env,
  fetchFn: FetchLike = globalThis.fetch as unknown as FetchLike
): CrmLeadPort {
  const cfg = mondayConfigFromEnv(env);
  if (!cfg) return NoopCrmLeadAdapter;
  return createMondayLeadAdapter(
    cfg,
    fetchFn,
    (contactId) =>
      // Human-facing link only; identity is the pulse id. Built off `MONDAY_HOST`
      // rather than a literal: an account rename is the scenario that constant
      // exists for, and a second copy of the host is a link that 404s while every
      // stored pointer stays correct.
      `${MONDAY_HOST}/boards/${cfg.contacts.boardId}/pulses/${contactId}`,
    presentation
  );
}
